import React, { ChangeEvent, FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Form, Formik } from 'formik';
import { Box, Button, Typography } from '@mui/material';

import { useSendSignOnEmailMutation, useSignOnByInvitationMutation } from 'api/public';
import {
  CheckEmail,
  ClientLogo,
  Layouts,
  Loaders,
  PaddedPaper,
  ValidatedTextFieldNext,
} from 'components';
import { useGlobalClient, useNotify } from 'hooks';
import { getAppImage } from 'helpers';
import cookieStorage from 'cookie-storage';

import { ClientRedirectSchema, IClientRedirectSchema } from '../validations';

const Passwordless: FC = () => {
  const notify = useNotify();
  const { t } = useTranslation();
  const client = useGlobalClient();
  const [showPasswordless, setShowPasswordless] = useState(false);
  const [initialValues, setInitialValues] = useState<IClientRedirectSchema>({
    username: cookieStorage.userEmail || '',
  });
  const invitationId = new URLSearchParams(location.search).get('t');
  const { mutate: signOnByInvitationMutate, isLoading: isSignOnByInvitationLoading } =
    useSignOnByInvitationMutation({
      onError: notify.mutationError,
      onSuccess: data => {
        const { signOnByInvitation } = data;
        const { invitation, token } = signOnByInvitation;
        if (token) {
          location.assign(`/?token=${token}`);
        } else {
          setInitialValues({ username: invitation.email_address });
        }
      },
    });
  useEffect(() => {
    if (invitationId) {
      signOnByInvitationMutate({ invitation_id: invitationId });
    }
  }, [invitationId]);
  const { mutate: sendSignOnMutation, isLoading } = useSendSignOnEmailMutation({
    onError: error => {
      const code = 'auth:' + error.toString().split('Error: ')[1];
      const translation = t(code);
      if (translation != code) {
        notify.error({ message: translation });
        return;
      }
      notify.mutationError(error as Error);
    },
    onSuccess: () => setShowPasswordless(true),
  });

  function handleSubmit(values: IClientRedirectSchema) {
    sendSignOnMutation({ email: values.username.toLowerCase(), client_id: client.id });
  }

  function handleChange(event: ChangeEvent<HTMLInputElement>) {
    cookieStorage.userEmail = event.target.value;
  }

  function renderImage() {
    return <img width={500} height={500} {...getAppImage('illustrations/boat.png', 500)} />;
  }

  function renderMain() {
    return (
      <PaddedPaper elevation={0} padding="64px 48px">
        <Formik
          onSubmit={handleSubmit}
          initialValues={initialValues}
          validationSchema={ClientRedirectSchema}
        >
          <Layouts.OnboardingBox component={Form}>
            <Box textAlign="center">
              <ClientLogo client={client} />
            </Box>
            <Typography align="center" paragraph variant="h2">
              {t('auth:clientSelectHeader')}
            </Typography>
            <Box paddingTop={3}>
              <ValidatedTextFieldNext
                enableTrim
                autoFocus
                fullWidth
                name="username"
                type="email"
                label={t('auth:clientSelectLabel')}
                placeholder={t('auth:clientSelectPlaceholder')}
                InputLabelProps={{ shrink: true }}
                onChange={handleChange}
              />
            </Box>
            <Box paddingTop={3}>
              <Button
                fullWidth
                type="submit"
                size="large"
                variant="contained"
                color="primary"
                disabled={isLoading}
              >
                {isLoading ? <Loaders.Standard height={26} /> : t('auth:letsGo')}
              </Button>
            </Box>
          </Layouts.OnboardingBox>
        </Formik>
      </PaddedPaper>
    );
  }

  if (isSignOnByInvitationLoading) {
    return <Loaders.Standard />;
  }

  return showPasswordless ? (
    <Layouts.Onboarding main={<CheckEmail />} />
  ) : (
    <Layouts.Onboarding image={renderImage()} main={renderMain()} />
  );
};

export default Passwordless;
